import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';

import Constants from '_app/constants';
import { MapFilterTabs } from '_app/constants/types';
import { publicIdSelector, userSelector } from '_app/features/session';
import * as propertyApi from '_app/features/property/shared/propertyApi';
import { propertyQueryKeys } from '_app/features/property/shared/propertyQueryKeys';
import { usePropertyInteractions } from '_app/features/property/shared/usePropertyInteractions';
import Utils from '_app/utils';

const DEFAULT_MAP_CENTER = { lat: 29.717971369549744, lng: -95.43397342215448 };

const mapFilterTabs = Object.values(MapFilterTabs);

const getTabByPath = (tabPath) => mapFilterTabs.find((tab) => tab.path === tabPath) || MapFilterTabs.NEW;

const getProfilePolygonBounds = (user) =>
  user?.search_polygons?.polygons?.map((polygon) =>
    polygon.latitude.map((lat, index) => ({
      lat,
      lng: polygon.longitude[index],
    })),
  ) || [];

export const useMapSearchController = (tabPath) => {
  const publicId = useSelector(publicIdSelector);
  const user = useSelector(userSelector);
  const currentTab = useMemo(() => getTabByPath(tabPath), [tabPath]);
  const hydratedPolygonsRef = useRef(false);

  const [toggleList, setToggleList] = useState(isMobile);
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [mapZoom, setMapZoom] = useState(Constants.DefaultMapZoom);
  const [mapReady, setMapReady] = useState(false);
  const [isMapDrawing, setIsMapDrawing] = useState(false);
  const [searchAsMove, setSearchAsMove] = useState(true);
  const [bounds, setBounds] = useState([]);
  const [preSelected, setPreSelected] = useState(null);
  const [previewPropertyId, setPreviewPropertyId] = useState(null);
  const [chatVisible, setChatVisible] = useState(false);
  const [chatThreadMlsnum, setChatThreadMlsnum] = useState(null);
  const [showActivities, setShowActivities] = useState(false);
  const [showOpenHouses, setShowOpenHouses] = useState(false);
  const { invalidatePropertyCollections, chatInteractions, propertyActions } = usePropertyInteractions({
    user,
    onPreApprovalSuccess: (mlsnum) => {
      setChatThreadMlsnum(mlsnum);
      setChatVisible(true);
    },
  });

  useEffect(() => {
    if (hydratedPolygonsRef.current || !user || Object.keys(user).length === 0) {
      return;
    }

    hydratedPolygonsRef.current = true;
    setBounds(getProfilePolygonBounds(user));
  }, [user]);

  const propertiesQuery = useInfiniteQuery({
    queryKey: propertyQueryKeys.collection(currentTab.apiEndpoint),
    queryFn: ({ pageParam = 1 }) => propertyApi.fetchProperties({ tab: currentTab.apiEndpoint, page: pageParam }),
    enabled: !!publicId && mapReady,
    getNextPageParam: (lastPage, pages) => {
      const loadedItems = pages.reduce((count, page) => count + (page.properties?.length || 0), 0);
      return lastPage.total_properties > loadedItems ? lastPage.page + 1 : undefined;
    },
  });

  const tabCountsQuery = useQuery({
    queryKey: propertyQueryKeys.tabCounts,
    queryFn: propertyApi.fetchPropertyTabCounts,
    enabled: !!publicId && mapReady,
  });

  const previewPropertyQuery = useQuery({
    queryKey: propertyQueryKeys.detail(previewPropertyId),
    queryFn: () => propertyApi.fetchProperty(previewPropertyId),
    enabled: !!publicId && !!previewPropertyId,
  });

  const chatQuery = useQuery({
    queryKey: propertyQueryKeys.chat(chatThreadMlsnum),
    queryFn: () => propertyApi.fetchChat(chatThreadMlsnum),
    enabled: !!publicId,
  });

  const activitiesQuery = useQuery({
    queryKey: propertyQueryKeys.recentActivities(),
    queryFn: () => propertyApi.fetchRecentActivities(),
    enabled: !!publicId && showActivities,
  });

  const openHousesQuery = useQuery({
    queryKey: propertyQueryKeys.openHouses(),
    queryFn: () => propertyApi.fetchUpcomingOpenHouses(),
    enabled: !!publicId && showOpenHouses,
  });

  useEffect(() => {
    if (!previewPropertyQuery.error) {
      return;
    }

    const errorMessage = Utils.getError(previewPropertyQuery.error).message;

    if (errorMessage.includes('not found')) {
      Utils.showErrorToast('This property is no longer available.');
    } else {
      Utils.showErrorToast(errorMessage);
    }

    setPreviewPropertyId(null);
    setChatThreadMlsnum(null);
    invalidatePropertyCollections();
  }, [invalidatePropertyCollections, previewPropertyQuery.error]);

  const addPropertyMutation = useMutation({
    mutationFn: propertyApi.addProperty,
    onSuccess: async (data, variables) => {
      await invalidatePropertyCollections();
      Utils.showInfoToast(`Added ${variables.address || `${data.added.length} properties`} to new.`);
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  const updatePolygonsMutation = useMutation({
    mutationFn: propertyApi.updateMapPolygons,
    onSuccess: async () => {
      await invalidatePropertyCollections();
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  const clearMapMutation = useMutation({
    mutationFn: propertyApi.deleteMapPolygons,
    onSuccess: async () => {
      await invalidatePropertyCollections();
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  const items = useMemo(() => propertiesQuery.data?.pages.flatMap((page) => page.properties || []) || [], [propertiesQuery.data]);
  const total = propertiesQuery.data?.pages?.[0]?.total_properties || 0;
  const success = propertiesQuery.isSuccess;

  return {
    publicId,
    user,
    currentTab,
    items,
    total,
    tabCounts: tabCountsQuery.data || {},
    loading: propertiesQuery.isLoading || (propertiesQuery.isFetching && !propertiesQuery.isFetchingNextPage),
    loadingMore: propertiesQuery.isFetchingNextPage,
    success,
    mapReady,
    isMapDrawing,
    searchAsMove,
    bounds,
    mapCenter,
    mapZoom,
    toggleList,
    preSelected,
    preview: {
      visible: !!previewPropertyId,
      item: previewPropertyQuery.data || null,
      loading: previewPropertyQuery.isLoading,
      error: previewPropertyQuery.error,
      onClose: () => {
        setPreviewPropertyId(null);
        setChatVisible(false);
        setChatThreadMlsnum(null);
      },
    },
    chat: {
      items: chatQuery.data || [],
      loading: chatQuery.isLoading || chatInteractions.loading,
      chatVisible,
      leadOwner: user?.lead_owner,
      userName: user?.name,
      onToggle: (value) => {
        if (value === 'open') {
          setChatVisible(true);
          return;
        }

        if (value === 'close') {
          setChatVisible(false);
          return;
        }

        setChatVisible((currentValue) => !currentValue);
      },
      onSend: (message) => chatInteractions.onSend(message, chatThreadMlsnum),
    },
    activities: {
      visible: showActivities,
      items: activitiesQuery.data || [],
      loading: activitiesQuery.isLoading,
      onOpen: () => setShowActivities(true),
      onClose: () => setShowActivities(false),
    },
    openHouses: {
      visible: showOpenHouses,
      items: openHousesQuery.data || [],
      loading: openHousesQuery.isLoading,
      onOpen: () => setShowOpenHouses(true),
      onClose: () => setShowOpenHouses(false),
    },
    propertyActions,
    onSearchManualAdd: propertyApi.searchManualAdd,
    onAddProperty: (payload) => addPropertyMutation.mutate(payload),
    onMapReady: () => setMapReady(true),
    onMapChange: ({ center, zoom }) => {
      setMapCenter(center);
      setMapZoom(zoom);
    },
    onMapZoomIn: () => setMapZoom((currentZoom) => (currentZoom < 22 ? currentZoom + 1 : currentZoom)),
    onMapZoomOut: () => setMapZoom((currentZoom) => (currentZoom > 3 ? currentZoom - 1 : currentZoom)),
    onToggleDrawing: () => setIsMapDrawing((currentValue) => !currentValue),
    onSetDrawing: setIsMapDrawing,
    onToggleList: () => setToggleList((currentValue) => !currentValue),
    onToggleSearchAsMove: () => setSearchAsMove((currentValue) => !currentValue),
    onLoadMore: () => {
      if (propertiesQuery.hasNextPage) {
        propertiesQuery.fetchNextPage();
      }
    },
    onRefreshProperties: () => propertiesQuery.refetch(),
    onPreSelectItem: (item) => setPreSelected(item),
    onMarkerClick: (item, isMobileView = false) => {
      if (!preSelected || preSelected.mlsnum !== item.mlsnum) {
        setPreSelected({ ...item, shouldScrollToItem: true });
        if (isMobileView) {
          setPreviewPropertyId(item.mlsnum);
          setChatVisible(false);
          setChatThreadMlsnum(item.mlsnum);
        }
        return;
      }

      setPreviewPropertyId(item.mlsnum);
      setChatVisible(false);
      setChatThreadMlsnum(item.mlsnum);
    },
    onOpenPreview: ({ mlsnum }) => {
      setPreviewPropertyId(mlsnum);
      setChatVisible(false);
      setChatThreadMlsnum(mlsnum);
    },
    onClearMap: () => {
      setBounds([]);
      setPreSelected(null);
      setMapCenter(DEFAULT_MAP_CENTER);
      setMapZoom(Constants.DefaultMapZoom);
      clearMapMutation.mutate();
    },
    onUpdateBounds: (nextBounds, shouldPersist = true) => {
      setBounds(nextBounds);
      setIsMapDrawing(false);

      if (shouldPersist) {
        updatePolygonsMutation.mutate(nextBounds);
      }
    },
  };
};
